import { UserData } from 'amazon-cognito-identity-js';
import { useAsyncRetry } from 'react-use';
import { useCallback, useMemo } from 'react';
import { AsyncRetryReturnType, ISsoConfiguration, ISsoEntityIdAndReplyUrl } from './types';
import { MutationTuple, QueryResult } from '@apollo/client/react/types/types';
import { MutationUpdaterFn, useMutation, useQuery, ApolloError } from '@apollo/client';
import { notification, useCognitoUser, useMe } from 'src/common';
import { useTranslation } from 'react-i18next';
import {
  UPDATE_MY_MFA_MODE,
  GET_GROUP_ID,
  GET_COMPANY_SSO_CONFIG,
  CREATE_SSO_CONFIG,
  UPDATE_SSO_CONFIG,
  CHECK_USER_LOGGED_TROUGH_SSO,
  GET_SSO_ENTITY_ID_AND_REPLY_URL,
  SETUP_AZURE_SSO,
  FETCH_COMPANY_ADMIN,
  DELETE_SSO_CONFIG
} from './queries';
import { MfaMode } from 'src/models/users/types';
import { FeatureFlag } from '@tendium/prom-types';
import { useFeatureFlag } from 'src/helpers';

export function useUserData(): AsyncRetryReturnType<UserData | undefined> {
  const { value: user, loading: userLoading, error: userError } = useCognitoUser();

  const getData = useCallback(async () => {
    if (!user) {
      return undefined;
    }
    return new Promise<UserData | undefined>((resolve, reject) => {
      user.getUserData((err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    });
  }, [user]);
  const { value, loading, error, retry } = useAsyncRetry(getData, [getData]);

  return useMemo(() => {
    if (loading || userLoading) {
      return {
        value: undefined,
        loading: true,
        error: undefined,
        retry
      };
    } else if (error || userError) {
      return {
        value: undefined,
        loading: false,
        error: error || userError,
        retry
      };
    } else {
      return {
        value,
        loading: false,
        error: undefined,
        retry
      };
    }
  }, [error, loading, retry, userError, userLoading, value]);
}

export interface IUpdateMFAModeValues {
  email: string;
  mfaMode: MfaMode | null;
}
export function useUpdateMFAMode(): MutationTuple<unknown, IUpdateMFAModeValues> {
  return useMutation(UPDATE_MY_MFA_MODE);
}

interface ISsoEntityIdAndReplyUrlResponse {
  getSsoEntityIdAndReplyUrl: ISsoEntityIdAndReplyUrl;
}
export function useGetSsoEntityIdAndReplyUrl(): QueryResult<ISsoEntityIdAndReplyUrlResponse> {
  return useQuery<ISsoEntityIdAndReplyUrlResponse>(GET_SSO_ENTITY_ID_AND_REPLY_URL);
}

export function useGroupSsoData(
  email?: string
): QueryResult<{ getSSOGroup: string | null }, { email: string | undefined }> {
  return useQuery(GET_GROUP_ID, {
    variables: { email },
    skip: !email
  });
}

interface ISsoConfigResponse {
  getCompanySSOConfiguration: ISsoConfiguration | null;
}
interface ISsoConfigInfo extends Pick<ISsoConfiguration, 'enforceSSO' | 'allowNewUserCreationFromSSO'> {
  loading: boolean;
  isSsoConfigExist?: boolean;
  activeSSO?: boolean;
  emailDomain?: string;
  provider?: string;
}
export function useSsoConfig(): ISsoConfigInfo {
  const { data: user } = useMe();

  const isSSOAvailable = useFeatureFlag(FeatureFlag.Auth_SSO);
  const { data, loading } = useQuery<ISsoConfigResponse>(GET_COMPANY_SSO_CONFIG, {
    skip: !user || !isSSOAvailable || !user.isAdmin
  });

  return useMemo(
    () => ({
      isSsoConfigExist: !!data && !!data.getCompanySSOConfiguration,
      activeSSO: data?.getCompanySSOConfiguration?.enabled,
      enforceSSO: data?.getCompanySSOConfiguration?.enforceSSO,
      allowNewUserCreationFromSSO: undefined,
      emailDomain: data?.getCompanySSOConfiguration?.emailDomain,
      provider: data?.getCompanySSOConfiguration?.provider,
      loading
    }),
    [data, loading]
  );
}

export function useCreateSsoConfig(): [
  (data: ISsoConfigPayload, onError?: () => void, onFinish?: () => void) => void,
  { loading: boolean; error?: ApolloError }
] {
  const { data: getMeData } = useMe();
  const companyId = getMeData?.company.id;

  const { t } = useTranslation();

  const [onCreateSsoConfig, { loading, error }] = useMutation(CREATE_SSO_CONFIG);

  const onCreateSsoConfigFn = useCallback(
    (data: ISsoConfigPayload, onError?: () => void, onFinish?: () => void) => {
      companyId &&
        onCreateSsoConfig({
          variables: {
            ...data
          },
          update: getUpdateCacheOnCreateSsoConfig()
        })
          .then(() => {
            onFinish && onFinish();
          })
          .catch(() => {
            onError && onError();
            notification.error({
              message: t('Common.unknownError'),
              description: t('Common.unknownErrorDesc')
            });
          });
    },
    [companyId, onCreateSsoConfig, t]
  );
  return [onCreateSsoConfigFn, { loading, error }];
}

export interface ISsoConfigPayload {
  enabled?: boolean;
  enforceSSO?: boolean;
  allowNewUserCreationFromSSO?: boolean;
  provider?: string;
}

export function useUpdateSsoConfig(): [
  (data: ISsoConfigPayload, onError?: () => void, onFinish?: () => void) => void,
  { loading: boolean; error?: ApolloError }
] {
  const { data: getMeData } = useMe();
  const companyId = getMeData?.company.id;
  const { t } = useTranslation();

  const [onUpdateSsoConfig, { loading, error }] = useMutation(UPDATE_SSO_CONFIG);

  const updateSsoConfigFn = useCallback(
    (data: ISsoConfigPayload, onError?: () => void, onFinish?: () => void) => {
      companyId &&
        onUpdateSsoConfig({
          variables: {
            ...data
          },
          update: getUpdateCacheOnUpdateSsoConfig()
        })
          .then(() => {
            onFinish && onFinish();
          })
          .catch(() => {
            onError && onError();
            notification.error({
              message: t('Common.unknownError'),
              description: t('Common.unknownErrorDesc')
            });
          });
    },
    [companyId, onUpdateSsoConfig, t]
  );
  return [updateSsoConfigFn, { loading, error }];
}

export function useDeleteSsoConfig(): [
  (emailDomain: string, onError?: () => void) => void,
  { loading: boolean; error?: ApolloError }
] {
  const { data: getMeData } = useMe();
  const companyId = getMeData?.company.id;
  const { t } = useTranslation();

  const [onDeleteSsoConfig, { loading, error }] = useMutation(DELETE_SSO_CONFIG);

  const deleteSsoConfigFn = useCallback(
    (emailDomain: string, onError?: () => void) => {
      companyId &&
        onDeleteSsoConfig({
          variables: {
            emailDomain
          },
          update: getUpdateCacheOnDeleteSsoConfig()
        }).catch(() => {
          onError && onError();
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [companyId, onDeleteSsoConfig, t]
  );
  return [deleteSsoConfigFn, { loading, error }];
}

export function getUpdateCacheOnUpdateSsoConfig(): MutationUpdaterFn<{
  updateSSOConfiguration: ISsoConfiguration;
}> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const queryData = cache.readQuery<ISsoConfigResponse>({
      query: GET_COMPANY_SSO_CONFIG
    });

    const company = queryData?.getCompanySSOConfiguration;
    if (!company) {
      return;
    }

    cache.writeQuery({
      query: GET_COMPANY_SSO_CONFIG,
      data: { getCompanySSOConfiguration: { ...data.updateSSOConfiguration } }
    });
  };
}

export function getUpdateCacheOnCreateSsoConfig(): MutationUpdaterFn<{
  createSSOConfiguration: ISsoConfiguration;
}> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }

    cache.writeQuery({
      query: GET_COMPANY_SSO_CONFIG,
      data: { getCompanySSOConfiguration: data.createSSOConfiguration }
    });
  };
}

export function getUpdateCacheOnDeleteSsoConfig(): MutationUpdaterFn {
  return (cache, { data }) => {
    if (!data) {
      return;
    }

    cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getCompanySSOConfiguration'
    });
    cache.gc();
  };
}

export function useCheckIfUserLoggedInThroughSSO(): QueryResult<{
  checkIfUserLoggedInThroughSSO: boolean;
}> {
  return useQuery(CHECK_USER_LOGGED_TROUGH_SSO);
}

export interface IGetCompanyAdmin {
  getCompany: {
    createdBy: {
      email: string;
      isAdmin: boolean;
    };
  };
}
export function useSsoEmailDomain(): { loading: boolean; data: string } {
  const { data: meData } = useMe();
  const { emailDomain, loading: configLoading } = useSsoConfig();
  const isAdmin = meData?.isAdmin;
  const companyId = meData?.company.id;

  const { data: companyData, loading: companyLoading } = useQuery<IGetCompanyAdmin>(FETCH_COMPANY_ADMIN, {
    variables: {
      companyId
    },
    skip: isAdmin || !!emailDomain
  });
  const companyAdminEmail = useMemo(
    () => (isAdmin ? meData && meData.email : companyData && companyData.getCompany.createdBy.email),
    [companyData, isAdmin, meData]
  );

  return useMemo(
    () => ({
      loading: configLoading || companyLoading,
      data: emailDomain ?? ((!!companyAdminEmail && companyAdminEmail.split('@')[1]) || '—')
    }),
    [companyAdminEmail, companyLoading, configLoading, emailDomain]
  );
}

export function useCreateAzureSsoConfig(): [
  (metadataUrl: string, onSuccess?: () => void) => void,
  { loading: boolean; error?: ApolloError }
] {
  const { t } = useTranslation();

  const [onCreateAzureConfig, { loading, error }] = useMutation<
    { setupAzureSamlSso: ISsoConfiguration },
    { samlMetaDataUrl: string }
  >(SETUP_AZURE_SSO);

  const onCreateSsoConfigFn = useCallback(
    (metadataUrl: string, onSuccess?: () => void) => {
      onCreateAzureConfig({
        variables: {
          samlMetaDataUrl: metadataUrl
        },
        update: getUpdateCacheOnCreateAzureConfig()
      })
        .then(() => {
          onSuccess && onSuccess();
        })
        .catch(() => {
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [onCreateAzureConfig, t]
  );
  return [onCreateSsoConfigFn, { loading, error }];
}

export function getUpdateCacheOnCreateAzureConfig(): MutationUpdaterFn<{
  setupAzureSamlSso: ISsoConfiguration;
}> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const queryData = cache.readQuery<ISsoConfigResponse>({
      query: GET_COMPANY_SSO_CONFIG
    });

    const company = queryData?.getCompanySSOConfiguration;
    if (!company) {
      return;
    }

    cache.writeQuery({
      query: GET_COMPANY_SSO_CONFIG,
      data: { getCompanySSOConfiguration: data.setupAzureSamlSso }
    });
  };
}
